<template>
<div>
  <div class="host centered-container my-2" data-testid="host-menu">
    <div v-if="user">
      <div class="mb-5 self-stretch relative w-full flex items-center justify-center gap-2">
      <header class="absolute left-0 top-1/2 -translate-y-1/2">
        <a href="javascript:;" @click="back" data-testid="host-back-button">
          <img src="@/assets/icons/arrow-left.svg" class="w-[25px]" />
        </a>
      </header>
        <div class="self-stretch flex flex-col text-center items-center gap-1">
            <div class="self-stretch text-center text-white text-2xl font-bold font-['Inter'] leading-[30px]">
            {{ heading }}
            </div>
        </div>
    </div>
      <div v-show="!createdState">
        <div class="form form-default">
          <form action="#" method="POST" @submit.prevent="createEvent" data-testid="session-form">
            <div class="form-row">
              <label for="#host_title" class="!pl-0 !font-medium">{{ $t('session_setup_title') }}</label>
              <input type="text" required name="title" v-model="title" class="form-input form-input--text !w-full !border-[#292F36]" placeholder="" id="host_title" data-testid="session-title-input">
            </div>

            <SwitchGroup as="div" class="flex justify-between mb-5">
              <span class="flex grow flex-col">
                <div class="flex items-center gap-2 mb-1">
                  <img src="@/assets/icons/sorenson/users.svg" alt="Logo" class="w-5 h-5 filter brightness-0 invert"/>
                  <SwitchLabel as="span" class="font-medium text-white" passive>{{ $t('audience_participation') }}</SwitchLabel>
                </div>
                <SwitchDescription as="span" class="text-sm text-neutral-400 ml-7">{{ $t('audience_participation_description') !== 'audience_participation_description' ? $t('audience_participation_description') : 'Enables audience voice and text input.' }}</SwitchDescription>
              </span>
              <Switch v-model="audience_participation" :class="[audience_participation ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']" data-testid="audience-participation-switch">
                <span aria-hidden="true" :class="[audience_participation ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
              </Switch>
            </SwitchGroup>

            <SwitchGroup as="div" class="flex justify-between mb-5">
              <span class="flex grow flex-col">
                <div class="flex items-center gap-2 mb-1">
                  <img src="@/assets/icons/transcripts-icon-clear.svg" alt="Logo" class="w-5 h-5 filter brightness-0 invert"/>
                  <SwitchLabel as="span" class="font-medium text-white" passive>
                    {{ $t('transcript_host') !== 'transcript_host' ? $t('transcript_host') : 'Transcript' }}
                  </SwitchLabel>
                </div>
                <SwitchDescription as="span" class="text-sm text-neutral-400 ml-7">
                  {{ $t('transcript_host_description') !== 'transcript_host_description' ? $t('transcript_host_description') : 'Save captions for after session.' }}
                </SwitchDescription>
              </span>
              <Switch v-model="save_transcripts" :class="[save_transcripts ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']" data-testid="transcript-switch">
                <span aria-hidden="true" :class="[save_transcripts ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
              </Switch>
            </SwitchGroup>

            <!-- Additional Settings -->
            <div @click="showAdditionalSettings = !showAdditionalSettings" class="min-h-[46px] flex items-center justify-between border-t border-[rgba(57,57,57,0.5)] cursor-pointer">
              <div class="flex items-center text-base leading-[1.18rem] text-white text-lg">
                <span>{{ $t('additional_settings') !== 'additional_settings' ? $t('additional_settings') : 'Additional settings' }}</span>
              </div>
              <img v-if="!showAdditionalSettings" src="@/assets/icons/chevron-down.svg" alt="Microphone"/>
              <img v-else src="@/assets/icons/chevron-up.svg" alt="Microphone"/>
            </div>

            <div v-show="showAdditionalSettings" class="mb-2"></div>

            <div class="border-b border-[rgba(57,57,57,0.5)] mb-5">
              <div v-show="showAdditionalSettings">
                <div class="form-row">
                  <label for="#display_name" class="!pl-0 !font-medium">{{ $t('session_setup_display_name') !== 'session_setup_display_name' ? $t('session_setup_display_name') : 'Display name' }}</label>
                  <input type="text" required name="display_name" v-model="displayName" class="form-input form-input--text !w-full !border-[#292F36]" placeholder="" id="host_title">
                </div>
                <SwitchGroup as="div" class="flex justify-between mb-5">
                  <span class="flex grow flex-col">
                    <SwitchLabel as="span" class="font-medium text-white" passive>{{ $t('custom_terms_profanity_filter') }}</SwitchLabel>
                    <SwitchDescription as="span" class="text-[12px] text-white opacity-75 max-w-[95%] md:max-w-72"></SwitchDescription>
                  </span>
                  <Switch v-model="profanity_filter" :class="[profanity_filter ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
                    <span aria-hidden="true" :class="[profanity_filter ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
                  </Switch>
                </SwitchGroup>
                <SwitchGroup as="div" class="flex justify-between mb-5">
                  <span class="flex grow flex-col">
                    <SwitchLabel as="span" class="font-medium text-white" passive>{{ $t('settings_autostop_title') }}</SwitchLabel>
                    <SwitchDescription as="span" class="text-sm text-neutral-400">{{ $t('session_setup_autostop_description') !== 'session_setup_autostop_description' ? $t('session_setup_autostop_description') : 'Microphone will stop after 30 minutes of silence to preserve caption/translation time' }}</SwitchDescription>
                  </span>
                  <Switch v-model="is_autostop_enabled" :class="[is_autostop_enabled ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
                    <span aria-hidden="true" :class="[is_autostop_enabled ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
                  </Switch>
                </SwitchGroup>
                <!-- Voice tone selection -->
                <div class="mb-3">
                  <div class="font-medium text-white">{{ $t('session_setup_voice_tone') !== 'session_setup_voice_tone' ? $t('session_setup_voice_tone') : 'Your voice tone' }}</div>
                  <div class="text-sm text-neutral-400">{{ $t('session_setup_voice_tone_desc') !== 'session_setup_voice_tone_desc' ? $t('session_setup_voice_tone_desc') : 'Select the tone you want particpants to hear when captions are read out loud' }}</div>
                </div>
                
                <div class="flex bg-[#282F35] rounded-xl h-[50px] mb-5">
                  <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" id="voice_male">
                        <input type="radio" v-model="voice" name="voice" value="male" class="hidden peer"/>
                        <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                              :class="voice === 'male' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
                          <svg class="mr-[10px] align-middle" width="18" height="18" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg">
                            <path :fill="voice==='male' ? '#6CBFFF' : '#C2C2C2'" d="M10.5 4.278C10.5 3.51572 10.4246 2.92207 10.2226 2.5332C10.1297 2.35444 10.0164 2.23292 9.87691 2.15104C9.73516 2.06786 9.52235 2 9.19201 2C9.0262 1.99993 8.86722 1.93817 8.7454 1.82812L8.70243 1.78516C8.70398 1.78683 8.70496 1.78832 8.70568 1.78906C8.70522 1.78861 8.70454 1.78788 8.70373 1.78711C8.698 1.78165 8.68366 1.76892 8.66076 1.75065C8.61474 1.71394 8.53315 1.65552 8.41271 1.5944C8.17506 1.47385 7.77044 1.33333 7.16141 1.33333C6.28311 1.33335 5.64308 1.59463 5.21935 2.03646C4.79252 2.48153 4.49995 3.19885 4.49995 4.278C4.49997 5.39597 4.68963 6.16207 4.98237 6.73893C5.27454 7.31463 5.69549 7.75354 6.24409 8.18294C6.40554 8.3093 6.49995 8.50332 6.49995 8.70833V10C6.49995 10.2942 6.3071 10.5539 6.02534 10.6387L3.55008 11.3822L3.55073 11.3828C2.91091 11.5787 2.4239 12.0648 2.21415 12.6667H12.7858C12.576 12.0648 12.089 11.5787 11.4492 11.3828L8.97456 10.6387C8.6928 10.5539 8.49995 10.2942 8.49995 10V8.70833C8.49995 8.50332 8.59437 8.3093 8.75581 8.18294C9.30441 7.75354 9.72536 7.31463 10.0175 6.73893C10.3103 6.16207 10.4999 5.39597 10.5 4.278ZM11.8333 4.278C11.8333 5.54854 11.6167 6.53306 11.2063 7.3418C10.8511 8.04175 10.3695 8.57066 9.83329 9.02409V9.50456L11.8359 10.1068L11.8391 10.1081C12.9896 10.4602 13.8462 11.3839 14.1308 12.5104C14.3571 13.4076 13.5687 13.9999 12.8958 14H2.10412C1.43131 13.9999 0.643236 13.4078 0.869744 12.5104C1.15429 11.3839 2.01028 10.4602 3.16076 10.1081L3.16402 10.1068L5.16662 9.50456V9.02409C4.6304 8.57066 4.14878 8.04176 3.79357 7.3418C3.38322 6.53306 3.16663 5.54854 3.16662 4.278C3.16662 2.96391 3.52611 1.8755 4.25711 1.11328C4.99119 0.347966 6.01483 1.54301e-05 7.16141 0C7.98616 0 8.59694 0.192844 9.01623 0.405599C9.20022 0.498962 9.34429 0.595106 9.45178 0.677083C9.86232 0.707788 10.2312 0.812947 10.552 1.0013C10.9414 1.2299 11.2164 1.55455 11.4056 1.91862C11.7675 2.61539 11.8333 3.49403 11.8333 4.278Z"/>
                          </svg>
                          {{ $t('enum_voicegender_male') }}
                        </span>
                  </label>
                  <label class="justify-center cursor-pointer w-1/2 flex items-center m-0 font-normal pl-0" id="voice_female">
                        <input type="radio" v-model="voice" name="voice" value="female" class="hidden peer"/>
                        <span class="peer-checked:bg-[#374D60] flex justify-center items-center h-full w-full rounded-xl"
                              :class="voice === 'female' ? 'text-[#6CBFFF]' : 'text-neutral-400'">
                          <svg class="mr-[10px] align-middle" width="18" height="18" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg">
                            <path :fill="voice==='female' ? '#6CBFFF' : '#C2C2C2'" d="M3.5 4.07488C3.50003 2.98743 3.75409 2.11223 4.20247 1.44728C4.65246 0.780055 5.27113 0.365308 5.92448 0.15952C6.57102 -0.0440616 7.24992 -0.0433244 7.84635 0.108088C8.30344 0.224126 8.74526 0.438223 9.09049 0.751317C9.5281 0.72135 9.93061 0.776728 10.2839 0.950535C10.7474 1.17863 11.0314 1.56165 11.2012 1.97918C11.365 2.38231 11.4337 2.84307 11.4668 3.29363C11.5002 3.74804 11.5 4.24541 11.5 4.73113C11.5 6.19625 11.9179 7.36157 12.0944 7.709C12.1805 7.87856 12.1898 8.07685 12.1204 8.25392C12.051 8.431 11.9095 8.57013 11.7311 8.63608C11.1534 8.84964 10.5144 9.01776 9.83333 9.13608V9.50392L11.8359 10.1068L11.8392 10.1074C12.9897 10.4595 13.8462 11.3833 14.1309 12.5098C14.357 13.4069 13.5687 14 12.8958 14H2.10417C1.4313 14 0.643121 13.4072 0.869791 12.5098C1.15442 11.3834 2.01033 10.4595 3.16081 10.1074L3.16406 10.1068L5.16667 9.50392V9.13608C4.48557 9.01776 3.8466 8.84964 3.26888 8.63608C3.09048 8.57013 2.94896 8.431 2.87956 8.25392C2.81021 8.07685 2.81952 7.87856 2.9056 7.709C3.17811 7.17276 3.5 6.29954 3.5 4.07488ZM4.83333 4.07488C4.83333 5.80483 4.64823 6.85533 4.39518 7.59832C4.86356 7.72907 5.37418 7.83288 5.91732 7.90171C6.25031 7.94397 6.49991 8.22751 6.5 8.56316V10C6.5 10.2942 6.30715 10.5539 6.02539 10.6387L3.55013 11.3822L3.55078 11.3828C2.91096 11.5788 2.42394 12.0648 2.21419 12.6667H12.7858C12.576 12.0648 12.089 11.5788 11.4492 11.3828L8.97461 10.6387C8.69285 10.5539 8.5 10.2942 8.5 10V8.56316C8.50009 8.22751 8.74969 7.94397 9.08268 7.90171C9.63112 7.83221 10.1464 7.72698 10.6185 7.59442C10.3915 6.90488 10.1667 5.89235 10.1667 4.73113C10.1667 4.23308 10.1665 3.78748 10.1374 3.39129C10.108 2.9912 10.0516 2.69296 9.96549 2.48113C9.88525 2.28385 9.7942 2.19516 9.69531 2.1465C9.58223 2.09088 9.3615 2.03992 8.94141 2.10874C8.69827 2.14859 8.45282 2.05135 8.30338 1.85548C8.16743 1.67715 7.89796 1.4968 7.51823 1.40041C7.14603 1.30593 6.72054 1.3067 6.32552 1.431C5.93744 1.5532 5.57661 1.79438 5.30794 2.19272C5.03762 2.59361 4.83337 3.19475 4.83333 4.07488Z"/>
                          </svg>
                          {{ $t('enum_voicegender_female') }}
                        </span>
                  </label>
                </div>
                
                <!-- Reading Speed selection -->
                <div class="min-h-[46px] flex items-center justify-between mb-5">
                  <div class="font-medium text-white">{{ $t('session_setup_speech_speed') !== 'session_setup_speech_speed' ? $t('session_setup_speech_speed') : 'Reading speed' }}</div>
                  <div>
                    <div class="flex items-center">
                      <span class="text-[0.875rem] leading-[1.375rem] text-[#C2C2C2] mr-[15px]">{{ settings.tts_speed }}%</span>
                      <div class="w-[96.73px] h-[35px] flex items-center bg-[#111111] rounded-[15px] px-0 py-[5px]">
                        <button type="button" class="bg-transparent outline-none shadow-none text-[1.3rem] leading-[1.375rem] text-center text-white w-1/2 cursor-pointer border-r border-white/15" @click="decrementSpeed">-</button>
                        <button type="button" class="bg-transparent border-none outline-none shadow-none text-[1.3rem] leading-[1.375rem] text-center text-white w-1/2 cursor-pointer" @click="incrementSpeed">+</button>
                      </div>
                    </div>
                  </div>
                </div>

                <!--Mute same language-->
                <SwitchGroup as="div" class="flex justify-between mb-5">
                  <span class="flex grow flex-col">
                    <SwitchLabel as="span" class="font-medium text-white" passive>{{ $t('session_setup_mute_same') !== 'session_setup_mute_same' ? $t('session_setup_mute_same') : 'Mute same language' }}</SwitchLabel>
                    <SwitchDescription as="span" class="text-sm text-neutral-400">{{ $t('session_setup_mute_same_description') !== 'session_setup_mute_same_description' ? $t('session_setup_mute_same_description') : 'Only other languages will be read aloud.' }}</SwitchDescription>
                  </span>
                  <Switch v-model="tts_same_muted" :class="[tts_same_muted ? 'bg-primary' : 'bg-gray-700', 'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden']">
                    <span aria-hidden="true" :class="[tts_same_muted ? 'translate-x-5 bg-gray-800' : 'translate-x-0 bg-white', 'pointer-events-none inline-block size-5 transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out mt-0.5']" />
                  </Switch>
                </SwitchGroup>

              </div>
            </div>

            <div class="self-stretch flex flex-col justify-start items-start gap-3 ">
              <button type="submit" name="host_submit" id="host_submit" value="Next" class="self-stretch px-5 py-2.5 bg-[#6CBFFF] hover:bg-opacity-30 rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden transition duration-160 cursor-pointer">
                  <div class="justify-start text-black text-base font-medium font-['Inter'] leading-normal">
                      {{ $t('start_session') !== 'start_session' ? $t('start_session') : 'Start session' }}
                  </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="event-start-screen" v-show="createdState">
        <event-start :is_host="true"></event-start>
      </div>
      <div class="centered-buttons" v-if="createdState && event">
        <router-link :to="'/room/' + event.room_code" class="orange-btn form-btn">{{ $t('common_start') }}</router-link>
        <a href="javascript:;" class="back-arrow-btn" @click="backToCreate">{{ $t('common_back') }}</a>
      </div>
  </div>
  <sheet-modal v-model="cantHostPopup" exact-width="w-[400px]" @closed="cantHostPopup = false">
    <modal-window :title="$t('cant_host_title')"
                  :description="$t('cant_host_description')"
                  icon="globe"
                  :primary-text="$t('start_trial')"
                  :secondary="true"
                  :secondary-text="$t('plans_view_plans')"
                  :cancel="true"
                  :cancel-text="$t('maybe_later')"
                  @closed="cantHostPopup = false"
                  @secondaryAction="goToPlans"
                  @commit="startTrial">
    </modal-window>
  </sheet-modal>
  <sheet-modal v-model="cantHostPopupWithoutTrialButton" exact-width="w-[400px]" @closed="cantHostPopupWithoutTrialButton = false">
    <modal-window :title="$t('times_up_title') !== 'times_up_title' ? $t('times_up_title') : 'Time\'s Up!'"
                  :description="$t('times_up_description') !== 'times_up_description' ? $t('times_up_description') : 'Looks like you\'ve used your translation time. Keep the conversations going by upgrading or grabbing more time.'"
                  icon="alarm-clock"
                  :primary-text="$t('plans_view_plans')"
                  :secondary="false"
                  :cancel="true"
                  :cancel-text="$t('maybe_later')"
                  @closed="cantHostPopupWithoutTrialButton = false"
                  @commit="goToPlans">
    </modal-window>
  </sheet-modal>
</div>
</template>

<script>
// @ is an alias to /src
import EventStart from '@/components/EventStart.vue'
import { mapState } from 'vuex';
import ModalWindow from "@/components/ui/ModalWindow.vue";
import SheetModal from "@/components/ui/SheetModal.vue";
import ProductService from "@/services/products.service";
import { PlanType } from "@/constants/pricing-page"
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue'

export default {
  name: 'HostView',
  components: {
    ModalWindow,
    EventStart,
    SheetModal,
    Switch,
    SwitchGroup,
    SwitchDescription,
    SwitchLabel,
  },
  data() {
    return {
      errors: {},
      title: this.getDefaultTitle(), 
      heading: this.$t('session_setup'),
      createdState: false,
      audience_participation: true,
      save_transcripts: true,
      profanity_filter: true,
      is_autostop_enabled: true,
      autostop_timeout: 1800,
      tts_same_muted: true,
      speech_mode: 'custom_pause',
      cantHostPopup: false,
      cantHostPopupWithoutTrialButton: false,
      showAdditionalSettings: false,
      displayName: '',
      voice: 'male',
      constraints: {
        tts_speed_max: 200,
        tts_speed_min: 50,
        tts_speed_step: 10
      },
    }
  },
  methods: {
    createEvent() {
      if (this.validate()) {
        if (this.displayName !== this.user.name || this.voice !== this.user.voice) {
        this.saveDisplayName();
      }
        this.$store.dispatch("event/create", {
          title: this.title,
          is_participating_disabled: !this.audience_participation,
          is_autostop_enabled: this.is_autostop_enabled,
          is_transcript_allowed: this.save_transcripts,
          autostop_timeout: this.autostop_timeout,
          speech_mode: this.speech_mode,
          profanity_filter: this.profanity_filter,
          tts_same_muted: this.tts_same_muted,
          tts_speed: this.settings.tts_speed,
        }).then(
          () => {
            this.startRoom();
          },
          (error) => {
            console.log(error)
            console.log(this.user)
            switch (error.response.data.error) {
              case 'number_of_allowed_events_exceeded':
                if (this.user.subscription.is_subscribed) {
                  this.cantHostPopupWithoutTrialButton = true;
                } 
                else {
                  this.cantHostPopup = true;
                }
                break;
              default:
                this.$toast.error(error.response.data.error);
            }
          }
        );
      }
    },
    saveDisplayName() {
      console.log(this.voice);
      const updatedProfile = {
        ...this.user,
        name: this.displayName,
        voice: this.voice,
      };
      
      this.$store.dispatch("auth/update", updatedProfile).then(
        (error) => {
          console.log("Error saving display name:", error);
        }
      );
    },
    getDefaultTitle() {
      const now = new Date();
      console.log(now);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const month = months[now.getMonth()];
      const day = now.getDate();
      const year = now.getFullYear();
      
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      
      return `${month} ${day}, ${year} ${hours}:${minutes} ${ampm}`;
    },
    incrementSpeed() {
      if (this.settings.tts_speed < this.constraints.tts_speed_max) {
        this.settings.tts_speed += this.constraints.tts_speed_step;
      }
    },
    decrementSpeed() {
      if (this.settings.tts_speed > this.constraints.tts_speed_min) {
        this.settings.tts_speed -= this.constraints.tts_speed_step
      }
    },
    validate() {
      // for now we are just validation passwords match
      // TODO other validation we need
      return true;
    },
    backToCreate() {
      this.createdState = false;
      this.heading = this.$t('session_setup');
    },
    startRoom() {
      this.selfClose();
      this.$router.push('/room/' + this.event.room_code);
    },
    setSpeechMode(mode) {
      this.speech_mode = mode;
    },
    goToPlans() {
      this.$router.push('/plans');
    },
    startTrial() {
      ProductService.requestTrial(this.token, PlanType.SORENSON_FREE_TRIAL)
        .then(() => {
          this.$toast.success("success_requested_trial")
        })
        .catch((error) => {
          this.$toast.error(error.response.data.error)
      })
      this.cantHostPopup = false;
    },
    back(){
       this.selfClose();
    },
    selfClose() {
      this.$emitter.emit('modalClose');
    }
  },
  computed: {
    ...mapState('auth', ['token', 'authorized']),
    ...mapState('lang', ['languages']),
    ...mapState('event', ['event', 'settings']),
    ...mapState('nonpersist', ['user']),
  },
  mounted() {

    if (this.settings && this.settings.speech_mode) {
      this.speech_mode = this.settings.speech_mode;
    }

    if (this.user && this.user.name){
      this.displayName = this.user.name;
    }
    if (this.user && this.user.voice) {
      this.voice = this.user.voice;
    }
  }
}
</script>

<style lang="scss" scoped>

  .host-form {
    width: 70%;

    input, button {
      width: 100% !important;
    }
  }
</style>
